import Application from "../../Application";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import CreateAttachmentTypeInteractor from "../interactor/CreateAttachmentTypeInteractor";
import UpdateAttachmentTypeInteractor from "../interactor/UpdateAttachmentTypeInteractor";
import { IAttachmentType } from "../model/AttachmentType";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import GetAllGenericAttachmentTypesInteractor from "../interactor/GetAllGenericAttachmentTypesInteractor";
import { IOrganisation } from "../../organisations/model/Organisation";

export interface TAttachmentTypePresenter extends TLoadingAwarePresenter {
  attachmentTypes: TAttachmentTypePresentationModel[];
  contentTypes: IGenericCodebookItem[];
  organisations: IOrganisation[];
}

export interface IAttachmentTypePresenter
  extends TAttachmentTypePresenter,
    TPresentable {
  loadAllAttachmentTypes(attachmentTypes: IAttachmentType[]): void;
  createAttachmentType(data: IAttachmentType): void;
  loadAttachmentTypeReferenceValues(
    contentTypes: IGenericCodebookItem[],
    organisations: IOrganisation[]
  ): void;
  updateAttachmentType(data: IAttachmentType): void;
}

const defaultState: TAttachmentTypePresenter = {
  attachmentTypes: [],
  contentTypes: [],
  organisations: []
};

const AttachmentTypePresenter = withStore<
  IAttachmentTypePresenter,
  TAttachmentTypePresenter
>(({ application, store, loader, translate }): IAttachmentTypePresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TAttachmentTypePresenter>();

  const loadAttachmentTypeReferenceValues = (
    contentTypes: IGenericCodebookItem[],
    organisations: IOrganisation[]
  ) => {
    _store.update({ contentTypes, organisations });
  };
  loader.start("attachmentTypeLoader");
  const loadAllAttachmentTypes = (attachmentTypesList: IAttachmentType[]) => {
    const { contentTypes, organisations } =
      _store.getState<TAttachmentTypePresenter>();
    let attachmentTypes = (attachmentTypesList || []).map((item) => {
      const contentType = contentTypes?.find(
        (value) => value.id === item.attachmentContentTypeId
      );
      const organisation = organisations?.find(
        (value) => item.organisationId && value.id === item.organisationId
      );
      return AttachmentTypePresentationModel(item, contentType, organisation);
    });
    _store.update({ attachmentTypes });
    loader.stop("attachmentTypeLoader");
  };

  const getAllAttachmentTypes = async () => {
    let attachmentTypes = await _application.container
      .resolve<GetAllGenericAttachmentTypesInteractor>(
        "getAllGenericAttachmentTypes"
      )
      .execute();
    loader.start("attachmentTypeLoader");
    loadAllAttachmentTypes(attachmentTypes);
  };

  const createAttachmentType = async (attachmentType: IAttachmentType) => {
    loader.start("attachmentTypeLoader");
    await _application.container
      .resolve<CreateAttachmentTypeInteractor>("createAttachmentType")
      .execute(attachmentType);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project_member.create_success_message"));

    await getAllAttachmentTypes();
    return true;
  };
  const updateAttachmentType = async (data: IAttachmentType) => {
    loader.start("attachmentTypeLoader");
    await _application.container
      .resolve<UpdateAttachmentTypeInteractor>("updateAttachmentType")
      .execute(data);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.beneficiary.update_success_message"));
    loader.stop("attachmentTypeLoader");
    await getAllAttachmentTypes();
    return true;
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadAllAttachmentTypes,
    createAttachmentType,
    loadAttachmentTypeReferenceValues,
    updateAttachmentType
  };
}, defaultState);

export default AttachmentTypePresenter;
